import { getAuthHeaders } from '../lib/setupAxiosAuth';

const API = '/api';
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 60 * 60 * 1000; // 1 hour safety cap

/**
 * Polls the crawl job status endpoint until the job reaches a terminal state.
 * Used as a fallback when the SSE stream is cut by an upstream proxy/timeout.
 * The backend continues processing and persists progress into MongoDB, so we
 * can resume near real-time progress updates here.
 *
 * @returns true when job completes successfully, false otherwise.
 */
export async function pollCrawlJobUntilDone(jobId, progressHandlers) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < MAX_POLL_DURATION_MS) {
    await sleep(POLL_INTERVAL_MS);
    const job = await fetchJob(jobId);
    if (!job) {
      continue;
    }
    applyJobProgress(job, progressHandlers);

    if (job.status === 'completed') {
      progressHandlers.setCompletedResult(job.result || { success: true, message: 'Traitement terminé' });
      progressHandlers.setCrawling(false);
      return true;
    }
    if (job.status === 'error') {
      throw new Error(job.error_message || 'Erreur côté serveur');
    }
  }
  return false;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchJob(jobId) {
  try {
    const response = await fetch(`${API}/documents/crawl-job/${jobId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch {
    return null;
  }
}

function applyJobProgress(job, progressHandlers) {
  const progress = job.progress || {};
  progressHandlers.setProgress((current) => ({
    ...current,
    ...progress,
    _polling: true,
    phase: job.phase,
  }));
}
